<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    persistent
    @hide="onDialogHide"
  >
    <q-card
      class="q-dialog-plugin session-card-dialog session-activity-dialog"
      :class="{ 'session-activity-dialog--mobile': $q.platform.is.mobile }"
      style="min-width:60vw"
    >
      <q-card-section class="row items-center no-wrap session-activity-dialog__header">
        <div
          class="text-h6 ellipsis col"
        >
          {{ dialogTitle }}
        </div>
        <div
          v-if="isLoading"
          class="col-auto q-mr-sm"
        >
          <q-spinner size="xs" />
        </div>
        <div
          v-if="showNav"
          class="col-auto row items-center no-wrap q-mr-xs"
        >
          <hr-icon-action-btn
            icon="chevron_left"
            aria-label="previous-activity"
            :disable="!navController.hasPrev || isNavBusy"
            @click="onNavPrev"
          >
            <hr-tooltip>{{ $t('common.buttonDirectionPrevious') }}</hr-tooltip>
          </hr-icon-action-btn>
          <hr-icon-action-btn
            icon="chevron_right"
            aria-label="next-activity"
            :loading="navController.isLoading"
            :disable="!navController.hasNext || isNavBusy"
            @click="onNavNext"
          >
            <hr-tooltip>{{ $t('common.buttonDirectionNext') }}</hr-tooltip>
          </hr-icon-action-btn>
        </div>
        <div class="col-auto">
          <q-btn
            flat
            round
            dense
            icon="close"
            class="col-auto"
            @click="onDialogClose"
          />
        </div>
      </q-card-section>
      <div
        ref="dialogBody"
        class="session-activity-dialog__body"
      >
        <q-card-section>
          <banner
            v-if="(
              hasIntegrationAppointment
              || canTransferStaffActivityWithoutLinkedAppointment(sessionActivity, true)
            )
              && !isParent"
            :type="appointmentBannerType"
            :message="appointmentBannerText"
            show-icon
            subtype="appointment"
            class="q-mb-md"
          >
            <template
              v-if="
                !isLoadingAppointment
                  && !isActivityRendered
              "
              #action
            >
              <div class="row items-center q-col-gutter-sm">
                <div
                  v-if="isReadyToSend"
                >
                  <q-btn
                    data-e2e="button-send-now"
                    :label="$t('sessions.sessionCard.buttonSendNow')"
                    :loading="isSendingSessionNote"
                    :disable="offlineMode"
                    flat
                    @click="onTransferSessionNote"
                  >
                    <q-tooltip v-if="offlineMode">
                      {{ $t('common.notAvailableOffline') }}
                    </q-tooltip>
                  </q-btn>
                </div>
              </div>
            </template>
          </banner>
        </q-card-section>
        <q-card-section>
          <session-activity-item
            v-if="hasSessionActivity"
            :session-activity="sessionActivity"
            expand
            bold
            hide-expand-icon
            :linked-appointment="linkedAppointment"
            :apply-immediately="applyImmediately"
            :has-other-notes-backup="false"
            :notes-sending="isSendingSessionNote"
            @session-activity-updated="onSessionActivityUpdated"
            @session-activity-closed="onSessionActivityClosed"
            @delete-request="onSessionActivityDeleteRequest"
            @changed="onChanged"
            @appointments-updated="onAppointmentUpdated"
            @send-to-integration="onTransferSessionNote"
          />
        </q-card-section>
      </div>
    </q-card>
  </q-dialog>
</template>
<script>
import SessionActivityItem from 'components/SessionActivities/SessionActivityItem';
import Banner from 'components/UI/Banner';
import { HrIconActionBtn, HrTooltip } from 'src/design-system';
import { get, isEmpty } from 'lodash';
import { mapState } from 'pinia';
import AppointmentsDB from 'src/fb/appointments-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import SessionExportMixin from 'src/mixins/SessionExportMixin';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'SessionActivityDialog',

  components: {
    SessionActivityItem,
    Banner,
    HrIconActionBtn,
    HrTooltip,
  },

  mixins: [Interactions, Integrations, SessionActivitiesMixin, SessionExportMixin],

  props: {
    sessionActivityId: {
      type: String,
      default: null,
    },
    closeOnCompleted: {
      type: Boolean,
      default: false,
    },
    applyImmediately: {
      type: Boolean,
      default: false,
    },
    // Controller from useActivityNavigation; when set, the card shows back/next.
    navController: {
      type: Object,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      isLoading: false,
      isBinded: false,
      isHiding: false,
      isLoadingAppointment: false,
      isSendingSessionNote: false,

      unsavedChanges: false,
      sessionActivityUpdated: false,
      sessionActivity: {},

      linkedAppointment: null,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'allowImmediateSync',
      'isParent']),
    ...mapState(network, ['offlineMode']),

    hasSessionActivity() {
      return !isEmpty(this.sessionActivity);
    },

    showNav() {
      return !!this.navController && (this.navController.hasPrev || this.navController.hasNext);
    },

    // Overlapping reads could resolve out of order, and a note being transferred
    // must finish against the activity it started on.
    isNavBusy() {
      return !!this.navController?.isLoading || this.isLoading || this.isSendingSessionNote;
    },

    effectiveSessionActivityId() {
      return this.navController?.currentId || this.sessionActivityId;
    },

    hasIntegrationAppointment() {
      return this.connectedClinic.integration && (get(this.sessionActivity, 'appointment', null) !== null);
    }, // hasIntegrationAppointment()

    isReadyToSend() {
      return (
        this.hasIntegrationAppointment
        && (
          this.sessionActivity?.status === 'closed'
          || (this.sessionActivity?.status === 'pending-signatures' && this.allowImmediateSync)
        )
      ) || this.canTransferStaffActivityWithoutLinkedAppointment(this.sessionActivity);
    },

    isActivityRendered() {
      return (
        this.sessionActivity?.status === 'rendered'
        || (
          this.allowImmediateSync
          && (
            this.linkedAppointment?.isRendered
            || (
              !!this.sessionActivity?.renderedTimestamp
              && this.sessionActivity?.appointmentSource
            )
          )
        )
      );
    },

    dialogTitle() {
      if (!this.hasSessionActivity) {
        return this.$t('common.loading');
      }
      return get(this.sessionActivity, 'sessionName') || this.getSessionActivityTypeLabel(this.sessionActivity);
    },

    appointmentBannerText() {
      if (this.isLoadingAppointment) {
        return this.$t('common.loading');
      }

      const integration = this.getIntegrationName(this.connectedClinic.integration);

      if (this.isSendingSessionNote) {
        return this.$t('activities.textTransferring', {
          integration,
        });
      }

      if (this.isActivityRendered) {
        return this.$t('activities.textActivityRendered', {
          integration,
        });
      }

      if (this.isReadyToSend) {
        return this.$t('activities.textTransferReady', {
          integration,
        });
      }

      return this.$t('activities.textTransferPendingNotes', {
        integration,
      });
    },

    appointmentBannerType() {
      if (!this.isActivityRendered && !this.isReadyToSend) {
        return 'warning';
      }
      if (!this.isActivityRendered && this.isReadyToSend) {
        return 'info';
      }
      if (this.isActivityRendered) {
        return 'success';
      }
      return 'info';
    }, // appointmentBannerType
  },

  watch: {
    'sessionActivity.appointment': {
      handler(appointmentId, prevAppointmentId) {
        if (appointmentId) {
          this.loadAppointment(appointmentId);
        } else if (prevAppointmentId) {
          this.linkedAppointment = null;
        }
      },
      immediate: true,
    },

    // Back/next moved to another standalone activity: reload in place. A dialog
    // that is already closing is being replaced, so the new id is not its business.
    'navController.currentId': {
      handler(newId, prevId) {
        if (newId && prevId && (newId !== prevId) && !this.isHiding) {
          this.onNavigateToActivity();
        }
      },
    },
  },

  created() {
    this.pendingActivityRequestId = null;
    this.pendingAppointmentRequestId = null;
  },

  methods: {
    async show() {
      this.$refs.dialog.show();
      await this.loadSessionActivity();
    },

    hide() {
      this.isHiding = true;
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    async onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      if (this.isBinded) {
        await this.unbindSessionActivity();
      }

      this.$emit('hide');
    }, // onDialogHide()

    onDialogClose() {
      if (this.unsavedChanges) {
        this.confirmDialog(
          this.$t('common.dialogTitleConfirm'),
          this.$t('sessions.sessionCard.confirmDiscardChanges'),
          async () => {
            if (this.sessionActivityUpdated) {
              this.$emit('ok');
            }
            this.hide();
          },
        );
        return;
      }
      if (this.sessionActivityUpdated) {
        this.$emit('ok');
      }
      this.hide();
    },

    onSessionActivityClosed() {
      if (this.closeOnCompleted) {
        if (this.sessionActivityUpdated) {
          this.$emit('ok');
        }
        this.hide();
      }
    },

    onSessionActivityUpdated() {
      this.sessionActivityUpdated = true;
      this.loadSessionActivity();
      // The edit can reorder or filter out the row we are navigating through.
      if (this.navController?.refreshRow) {
        this.navController.refreshRow(this.effectiveSessionActivityId);
      }
    },

    onNavPrev() {
      this.guardedNavigate(() => this.navController.goPrev());
    },

    onNavNext() {
      this.guardedNavigate(() => this.navController.goNext());
    },

    guardedNavigate(navigate) {
      if (this.isNavBusy) {
        return;
      }
      if (this.unsavedChanges) {
        this.confirmDialog(
          this.$t('common.dialogTitleConfirm'),
          this.$t('sessions.sessionCard.confirmDiscardChanges'),
          navigate,
        );
        return;
      }
      navigate();
    },

    async onNavigateToActivity() {
      if (this.isBinded) {
        await this.unbindSessionActivity();
      }
      this.unsavedChanges = false;
      this.sessionActivity = {};
      this.linkedAppointment = null;
      // Drop any appointment read still in flight for the previous activity.
      this.pendingAppointmentRequestId = null;
      await this.loadSessionActivity();
      this.resetScroll();
    },

    resetScroll() {
      if (this.$refs.dialogBody) {
        this.$refs.dialogBody.scrollTop = 0;
      }
    },

    onAppointmentUpdated() {
      this.loadAppointment(this.sessionActivity.appointment);
    },

    async onSessionActivityDeleteRequest(sessionActivity) {
      await this.deleteSessionActivity(sessionActivity);
      this.notifySuccess(this.$t('sessions.sessionCard.notifyDeleteSessionActivity'));
      this.$emit('ok', true);
      this.hide();
    },

    onChanged(isChanged) {
      this.unsavedChanges = isChanged;
    },

    async onTransferSessionNote(sessionActivityId = null, overrideAppointmentData = null) {
      await this.loadSessionActivity();
      if (this.sessionActivity?.id === sessionActivityId && overrideAppointmentData) {
        this.sessionActivity = {
          ...this.sessionActivity,
          ...overrideAppointmentData,
        };
      }

      if (
        this.isSendingSessionNote
        || !this.hasSessionActivity
        || (
          !this.hasIntegrationAppointment
          && !this.canTransferStaffActivityWithoutLinkedAppointment(this.sessionActivity, true)
        )
        || (
          this.hasIntegrationAppointment
          && this.sessionActivity?.status !== 'closed'
          && !(this.sessionActivity?.status === 'pending-signatures' && this.allowImmediateSync)
        )
      ) {
        return;
      }
      this.isSendingSessionNote = true;
      try {
        if (this.sessionActivity?.status === 'rendered') {
          throw new Error('Activity is already rendered');
        }

        await this.transferSessionActivityNote(this.sessionActivity);

        if (this.hasIntegrationAppointment) {
          await this.loadAppointment(this.sessionActivity.appointment);
        } else {
          // in case of sending staff activity without a linked appointment, we need to bind the activity
          // to update it in real time with the new appointment created in the integration
          await this.bindSessionActivity();
        }

        this.notifySuccess(this.$t('sessions.sessionCard.notifyTransferSuccess', {
          integration: this.getIntegrationName(this.connectedClinic.integration),
        }));

        this.$emit('ok', true);
      } catch (err) {
        this.handleTransferError(err);
        logEvent(err);
      } finally {
        this.isSendingSessionNote = false;
        this.loadSessionActivity();
      }
    }, // onTransferSessionNote()

    async transferSessionActivityNote(sessionActivity) {
      const { base64pdf } = await this.getSessionActivityNotesDataToTransfer(sessionActivity);

      return this.transferSessionToIntegration(
        sessionActivity.appointment,
        sessionActivity,
        base64pdf,
      );
    }, // transferSessionActivityNote()

    async loadSessionActivity() {
      if (this.isBinded) {
        return;
      }

      const requestedId = this.effectiveSessionActivityId;
      this.pendingActivityRequestId = requestedId;

      this.isLoading = true;
      const sessionActivitiesDb = new SessionActivitiesDB();
      const sessionActivity = await sessionActivitiesDb.read(requestedId);

      if (this.pendingActivityRequestId !== requestedId) {
        return;
      }

      this.sessionActivity = sessionActivity;
      this.isLoading = false;
    }, // loadSessionActivity()

    async bindSessionActivity() {
      const sessionActivitiesDb = new SessionActivitiesDB();
      await sessionActivitiesDb.bindDocument(this, 'sessionActivity', this.effectiveSessionActivityId);
      this.isBinded = true;
    }, // bindSessionActivity()

    async unbindSessionActivity() {
      const sessionActivitiesDb = new SessionActivitiesDB();
      await sessionActivitiesDb.unbind(this, 'sessionActivity');
      this.isBinded = false;
    }, // unbindSessionActivity()

    async loadAppointment(appointmentId) {
      if (!appointmentId) {
        return;
      }
      this.pendingAppointmentRequestId = appointmentId;

      this.isLoadingAppointment = true;
      const appointmentsDb = new AppointmentsDB();
      const appointment = await appointmentsDb.read(appointmentId);

      if (this.pendingAppointmentRequestId !== appointmentId) {
        return;
      }

      this.linkedAppointment = appointment;
      this.isLoadingAppointment = false;
    }, // loadAppointment()
  },
};
</script>

<style scoped>
#session-card-dialog {
  min-width: 80vw;
  max-width: unset;
}

.session-activity-dialog {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.session-activity-dialog--mobile {
  max-height: none;
  height: 100%;
}

.session-activity-dialog__header {
  flex: 0 0 auto;
}

.session-activity-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
</style>
